// src/pages/Signup.jsx
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useToast,
  Image,
  Checkbox,
  Link as ChakraLink,
  HStack,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth';
import { auth } from '../firebaseConfig';

// Services for SoT flow
import {
  createOrUpdatePendingOrg,
  readPendingOrg,
  deletePendingOrg,
} from '../services/signup/pendingOrgsService';
import { bootstrapSelfOrgAndRefresh } from '../services/signup/bootstrapService';

/** Feature flags (UI-only): hide sections without removing logic */
const SHOW_SOCIAL = false;
const SHOW_BUSINESS_TYPE = false;
const SHOW_WEBSITE_AND_MOBILE = false;

// Google button (logic kept; UI hidden via SHOW_SOCIAL flag)
const GoogleButton = ({ onClick, isLoading, isDisabled }) => {
  const enabled = !isLoading && !isDisabled;

  const disabledBg = useColorModeValue('gray.100', 'gray.700');
  const disabledBorderColor = useColorModeValue('gray.200', 'gray.600');
  const disabledColor = useColorModeValue('gray.400', 'gray.500');

  const googleSvg = (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 11.99v3.04h6.32c-.17 1.04-.82 2.57-2.35 3.66-1.08.77-2.46 1.31-3.97 1.31-3.04 0-5.61-2.04-6.52-4.78a6.77 6.77 0 0 1 0-4.46C6.39 7.02 8.96 4.98 12 4.98c1.74 0 2.9.74 3.58 1.38l2.44-2.36C16.76 2.72 14.57 1.8 12 1.8 7.57 1.8 3.83 4.46 2.4 8.28a10.2 10.2 0 0 0 0 7.44C3.83 19.54 7.57 22.2 12 22.2c2.88 0 5.3-.94 7.07-2.71 2.03-2.03 2.58-4.9 2.58-7.2 0-.72-.06-1.44-.19-2.04H12z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <Button
      onClick={onClick}
      isLoading={isLoading}
      isDisabled={isDisabled}
      leftIcon={googleSvg}
      width="100%"
      height="48px"
      bg={enabled ? 'white' : disabledBg}
      color={enabled ? 'gray.700' : disabledColor}
      border={enabled ? '1px solid' : 'none'}
      borderColor={enabled ? 'gray.300' : disabledBorderColor}
      boxShadow={enabled ? 'sm' : 'none'}
      borderRadius="lg"
      _hover={
        enabled
          ? {
              bg: 'gray.50',
              borderColor: 'gray.400',
              boxShadow: 'md',
            }
          : {}
      }
      _active={
        enabled
          ? {
              bg: 'gray.100',
              borderColor: 'gray.500',
              boxShadow: 'sm',
            }
          : {}
      }
      _disabled={{
        cursor: 'not-allowed',
        color: disabledColor,
        bg: disabledBg,
        borderColor: disabledBorderColor,
        boxShadow: 'none',
        opacity: 0.8,
      }}
      mb={2}
    >
      Continue with Google
    </Button>
  );
};

export default function Signup() {
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [businessType, setBusinessType] = useState('company');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // UI state
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  // Required core fields (no password) → gates Google
  const isCoreValid = useMemo(() => {
    return (
      companyName.trim() !== '' &&
      (businessType === 'company' || businessType === 'sole') &&
      firstName.trim() !== '' &&
      lastName.trim() !== '' &&
      email.trim() !== '' &&
      agreed
    );
  }, [companyName, businessType, firstName, lastName, email, agreed]);

  // Password rule (≥10) for enabling the Sign up button
  const isPasswordValid = password && password.length >= 10;

  // Button enabled states
  const googleEnabled = isCoreValid && !loading && !googleLoading;
  const emailSignupEnabled = isCoreValid && isPasswordValid && !loading;

  // Resolve colors for signup button unconditionally
  const signupEnabledColor = useColorModeValue('white', 'white');
  const signupDisabledBg = useColorModeValue('gray.200', 'gray.600');
  const signupDisabledColor = useColorModeValue('gray.400', 'gray.500');

  // Card & page colours
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const pageBg = useColorModeValue('gray.50', 'gray.900');

  // 👇 Moved out of conditional render to satisfy rules-of-hooks
  const orTextColor = useColorModeValue('gray.600', 'gray.300');

  // Resume prefill if pending doc exists
  useEffect(() => {
    const run = async () => {
      if (!auth.currentUser) return;
      try {
        const snap = await readPendingOrg(auth.currentUser.uid);
        if (snap?.exists()) {
          const d = snap.data();
          setCompanyName(d.companyName || '');
          setBusinessType(d.traderType || 'company');
          setFirstName(d.firstName || '');
          setLastName(d.lastName || '');
          setMobile(d.mobile || '');
          setWebsite(d.website || '');
          setEmail(d.email || '');
          setAgreed(true);
          toast({
            title: 'Setup found',
            description: 'We prefilled your details.',
            status: 'info',
          });
        }
      } catch {
        // ignore
      }
    };
    run();
  }, [toast]);

  // Save to pendingOrgs and return payload (used for callable)
  const savePending = useCallback(
    async (uid) => {
      const payload = {
        companyName: companyName.trim(),
        traderType: businessType,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        mobile: mobile.trim(),
        website: website.trim(),
        termsAcceptedAt: new Date().toISOString(),
      };
      await createOrUpdatePendingOrg(uid, payload);
      try {
        sessionStorage.setItem(
          'signupPrefill',
          JSON.stringify({
            traderType: payload.traderType,
            companyName: payload.companyName,
            email: payload.email,
            mobile: payload.mobile,
            website: payload.website,
          })
        );
      } catch {}
      return payload;
    },
    [companyName, businessType, firstName, lastName, email, mobile, website]
  );

  // Build a "rich" server payload to satisfy backend schema
  const toServerPayload = (p) => ({
    companyName: p.companyName,
    traderType: p.traderType,
    businessType: p.traderType,
    owner: {
      firstName: p.firstName,
      lastName: p.lastName,
      email: p.email,
      mobile: p.mobile,
      phone: p.mobile,
      website: p.website,
    },
    // Flat duplicates (compat)
    firstName: p.firstName,
    lastName: p.lastName,
    email: p.email,
    mobile: p.mobile,
    website: p.website,
    termsAcceptedAt: p.termsAcceptedAt || new Date().toISOString(),
  });

  const runBootstrap = useCallback(
    async (uid) => {
      const pending = await savePending(uid);
      const payload = toServerPayload(pending);
      const result = await bootstrapSelfOrgAndRefresh(payload);
      if (result?.ok) {
        await deletePendingOrg(uid).catch(() => {});
        navigate('/settings/sales-people', { replace: true });
      } else {
        throw new Error(
          result?.message || 'We couldn’t finish setting up your organisation. Please try again.'
        );
      }
    },
    [navigate, savePending]
  );

  // Email + password path
  const handleEmailSignup = async (e) => {
    e.preventDefault();
    if (!emailSignupEnabled) {
      if (!isCoreValid) {
        toast({
          title: 'Please complete all required fields and accept Terms',
          status: 'warning',
        });
      } else if (!isPasswordValid) {
        toast({
          title: 'Password must be at least 10 characters',
          status: 'warning',
        });
      }
      return;
    }
    try {
      setLoading(true);
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      await user.getIdToken(true);
      await updateProfile(user, { displayName: `${firstName} ${lastName}` }).catch(() => {});
      await runBootstrap(user.uid);
    } catch (e) {
      const msg = (e && e.message) || 'Sign up failed. Please try again.';
      toast({ title: 'Sign up failed', description: msg, status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Google path (gated by core validity only) — logic retained but UI hidden
  const handleGoogleSignup = async () => {
    if (!googleEnabled) return;
    try {
      setGoogleLoading(true);
      const provider = new GoogleAuthProvider();
      const { user } = await signInWithPopup(auth, provider);
      await user.getIdToken(true);
      await runBootstrap(user.uid);
    } catch (e) {
      const msg = (e && e.message) || 'Google sign-in failed. Please try again.';
      toast({ title: 'Google sign-in failed', description: msg, status: 'error' });
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <Center minH="100vh" bg={pageBg} p={6}>
      <Box
        w="100%"
        maxW="560px"
        bg={cardBg}
        borderWidth="1px"
        borderRadius="lg"
        boxShadow="md"
        borderColor={borderColor}
        p={8}
      >
        <Stack spacing={6}>
          {/* Logo */}
          <Image src="/images/logo.png" alt="Fixihub Logo" maxW="200px" mx="auto" />

          {/* Heading */}
          <Heading textAlign="center" size="lg">
            Create your account
          </Heading>

          <form onSubmit={handleEmailSignup}>
            <Stack spacing={5}>
              {/* Company details */}
              <FormControl isRequired>
                <FormLabel>Company</FormLabel>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder=""
                />
              </FormControl>

              {/* Business type — HIDDEN (defaults to "company") */}
              {SHOW_BUSINESS_TYPE && (
                <FormControl isRequired>
                  <FormLabel>Business type</FormLabel>
                  {/* Radios hidden intentionally */}
                </FormControl>
              )}

              {/* Website & Mobile — HIDDEN, values still stored/sent */}
              {SHOW_WEBSITE_AND_MOBILE && (
                <HStack spacing={4} flexDir={{ base: 'column', md: 'row' }}>
                  <FormControl>
                    <FormLabel>Website</FormLabel>
                    <Input
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      placeholder="e.g. https://example.co.uk"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Mobile (easy login)</FormLabel>
                    <Input
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      placeholder="e.g. 07…"
                    />
                  </FormControl>
                </HStack>
              )}

              {/* Your details */}
              <HStack spacing={4} flexDir={{ base: 'column', md: 'row' }}>
                <FormControl isRequired>
                  <FormLabel>First name</FormLabel>
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Last name</FormLabel>
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </FormControl>
              </HStack>

              {/* Login details */}
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder=""
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Password.</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="minimum 10 digits"
                  minLength={10}
                />
              </FormControl>

              <FormControl isRequired>
                <Checkbox isChecked={agreed} onChange={(e) => setAgreed(e.target.checked)}>
                  I agree to the{' '}
                  <ChakraLink
                    as={RouterLink}
                    to="/terms"
                    color="blue.500"
                    textDecoration="underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms & Conditions
                  </ChakraLink>
                </Checkbox>
              </FormControl>

              {/* Sign up */}
              <Button
                type="submit"
                isLoading={loading}
                isDisabled={!emailSignupEnabled}
                height="48px"
                borderRadius="xl"
                bg={
                  emailSignupEnabled
                    ? 'linear-gradient(145deg, var(--chakra-colors-blue-400), var(--chakra-colors-blue-500))'
                    : signupDisabledBg
                }
                color={signupEnabledColor}
                boxShadow={emailSignupEnabled ? 'md' : 'none'}
                _hover={
                  emailSignupEnabled
                    ? {
                        bg: 'linear-gradient(145deg, var(--chakra-colors-blue-500), var(--chakra-colors-blue-700))',
                        boxShadow: 'lg',
                      }
                    : {}
                }
                _active={
                  emailSignupEnabled
                    ? {
                        bg: 'linear-gradient(145deg, var(--chakra-colors-blue-700), var(--chakra-colors-gray-800))',
                        boxShadow: 'sm',
                      }
                    : {}
                }
                _disabled={{
                  cursor: 'not-allowed',
                  color: signupDisabledColor,
                  bg: signupDisabledBg,
                  boxShadow: 'none',
                }}
              >
                Per Aspera Ad Astra
              </Button>
            </Stack>
          </form>

          {/* OR separator — HIDDEN while social is off */}
          {SHOW_SOCIAL && (
            <HStack mt={4} mb={2} justifyContent="center" pos="relative">
              <Divider borderColor="gray.300" />
              <Text
                pos="absolute"
                bg={cardBg}
                px="4"
                fontSize="sm"
                color={orTextColor}
              >
                OR
              </Text>
              <Divider borderColor="gray.300" />
            </HStack>
          )}

          {/* Google button — HIDDEN while social is off */}
          {SHOW_SOCIAL && (
            <GoogleButton
              onClick={handleGoogleSignup}
              isLoading={googleLoading}
              isDisabled={!googleEnabled}
            />
          )}
          <Text textAlign="center" fontSize="sm" mt={1}>
            Already have an account?{' '}
            <ChakraLink as={RouterLink} to="/login" color="blue.500" fontWeight="semibold">
              Log In
            </ChakraLink>
          </Text>
        </Stack>
      </Box>
    </Center>
  );
}